// core/services/videoUploadService.ts — video phase 2 (2026-09-26): upload a prepared clip.
//
// The backend never carries video bytes. It hands out one presigned URL per 10 MiB part and
// the browser PUTs each part straight to the video bucket (R2). Each URL is signed for the
// part's exact length, so a part of any other size is refused by R2 itself.
//
//   limits()  → what this member may upload (and whether video is switched on at all)
//   upload()  → poster → start → parts (3 at a time, retried, re-signed) → complete → PostVideo
//
// Parts go through XMLHttpRequest, not fetch: only XHR reports upload progress. The R2
// request carries no cookies (withCredentials stays false) — the signature IS the auth.

import { api, ApiError } from '@/core/lib/apiClient';
import type { PostVideo } from '@/core/models/post';
import { mediaService } from './mediaService';

export interface VideoLimits {
  /** False until the owner switches video on (or the bucket is unconfigured). */
  enabled: boolean;
  /** Verified email AND confirmed 2FA. */
  canUpload: boolean;
  tier: 'free' | 'pro';
  maxSeconds: number;
  maxLongSide: number;
  maxShortSide: number;
  maxBytes: number;
  videoBitrate: number;
  lightLongSide: number;
  lightShortSide: number;
  lightMaxBytes: number;
  lightVideoBitrate: number;
  audioBitrate: number;
  dailyLimit: number;
  usedToday: number;
  quotaBytes: number;
  usedBytes: number;
}

export type Rendition = 'full' | 'light';

interface PartTicket {
  partNumber: number;
  bytes: number;
  url: string;
}

interface StartResponse {
  id: string;
  partSize: number;
  expiresInSeconds: number;
  full: { bytes: number; parts: PartTicket[] };
  light: { bytes: number; parts: PartTicket[] } | null;
}

/** A clip ready to upload: what videoPrepare produces. */
export interface PreparedVideo {
  full: Blob;
  /** The 720p feed copy; null when the clip is already 720p or smaller. */
  light: Blob | null;
  durationMs: number;
  width: number;
  height: number;
  poster: Blob | null;
}

export interface UploadHooks {
  /** 0..1 across every byte of every part. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** Thrown when an upload is cancelled through its AbortSignal. */
export class UploadAbortedError extends Error {
  constructor() {
    super('aborted');
    this.name = 'UploadAbortedError';
  }
}

/** A part PUT that failed at the network or HTTP level — retryable. */
class PartFailedError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`part upload failed (${status})`);
    this.status = status;
  }
}

function putPartXhr(
  url: string,
  body: Blob,
  onBytes: (sent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => onBytes(e.loaded);
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new PartFailedError(xhr.status));
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(new PartFailedError(0));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(new UploadAbortedError());
    };
    signal?.addEventListener('abort', onAbort);
    xhr.send(body);
  });
}

/** Test seam: the one function that talks to R2. */
export const videoTransport = { putPart: putPartXhr };

const CONCURRENCY = 3;
const PART_ATTEMPTS = 4;
const RETRY_BASE_MS = 800;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new UploadAbortedError());
    });
  });

async function uploadRendition(
  id: string,
  rendition: Rendition,
  blob: Blob,
  partSize: number,
  tickets: PartTicket[],
  report: (partKey: string, sent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const queue = [...tickets];
  const worker = async () => {
    for (let ticket = queue.shift(); ticket; ticket = queue.shift()) {
      const start = (ticket.partNumber - 1) * partSize;
      const body = blob.slice(start, start + ticket.bytes);
      const key = `${rendition}:${ticket.partNumber}`;
      let url = ticket.url;
      for (let attempt = 1; ; attempt += 1) {
        try {
          await videoTransport.putPart(url, body, (sent) => report(key, sent), signal);
          report(key, ticket.bytes);
          break;
        } catch (e) {
          if (e instanceof UploadAbortedError || signal?.aborted) throw new UploadAbortedError();
          if (attempt >= PART_ATTEMPTS) throw e;
          report(key, 0);
          await sleep(RETRY_BASE_MS * 2 ** (attempt - 1), signal);
          // A 403 usually means the URL expired (they live an hour) — re-sign before retrying.
          const fresh = await videoUploadService.parts(id, rendition, [ticket.partNumber]);
          url = fresh.parts[0]?.url ?? url;
        }
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, tickets.length) }, worker));
}

export const videoUploadService = {
  limits(): Promise<VideoLimits> {
    return api<VideoLimits>('/media/videos/limits/');
  },

  parts(id: string, rendition: Rendition, partNumbers: number[]): Promise<{ parts: PartTicket[] }> {
    return api(`/media/videos/${id}/parts/`, {
      method: 'POST',
      body: { rendition, partNumbers },
    });
  },

  /** Discard an upload (or an unposted ready clip). Best effort — the server sweeps too. */
  async cancel(id: string): Promise<void> {
    try {
      await api(`/media/videos/${id}/`, { method: 'DELETE' });
    } catch {
      /* the hourly sweep and R2's lifecycle rule close anything left open */
    }
  },

  async upload(input: PreparedVideo, hooks: UploadHooks = {}): Promise<PostVideo> {
    const { signal, onProgress } = hooks;
    const total = input.full.size + (input.light?.size ?? 0);
    const sentByPart = new Map<string, number>();
    const report = (key: string, sent: number) => {
      sentByPart.set(key, sent);
      let sum = 0;
      for (const v of sentByPart.values()) sum += v;
      onProgress?.(total ? Math.min(1, sum / total) : 1);
    };

    let posterKey: string | undefined;
    if (input.poster) {
      // A missing thumbnail must never cost the member their video.
      try {
        const file = new File([input.poster], 'poster.jpg', { type: input.poster.type });
        posterKey = (await mediaService.upload('poster', file)).key;
      } catch {
        posterKey = undefined;
      }
    }
    if (signal?.aborted) throw new UploadAbortedError();

    const started = await api<StartResponse>('/media/videos/', {
      method: 'POST',
      body: {
        full: { bytes: input.full.size },
        light: input.light ? { bytes: input.light.size } : null,
        durationMs: Math.round(input.durationMs),
        width: input.width,
        height: input.height,
      },
    });
    try {
      await uploadRendition(
        started.id,
        'full',
        input.full,
        started.partSize,
        started.full.parts,
        report,
        signal,
      );
      if (input.light && started.light) {
        await uploadRendition(
          started.id,
          'light',
          input.light,
          started.partSize,
          started.light.parts,
          report,
          signal,
        );
      }
      if (signal?.aborted) throw new UploadAbortedError();
      return await api<PostVideo>(`/media/videos/${started.id}/complete/`, {
        method: 'POST',
        body: posterKey ? { posterKey } : {},
      });
    } catch (e) {
      // Anything but a server-side rejection (which already cleaned up) → release the upload.
      if (!(e instanceof ApiError && e.status === 400)) void videoUploadService.cancel(started.id);
      throw e;
    }
  },
} as const;

/** Plain-language text for every coded video error the server can return. */
export function videoErrorMessage(e: unknown): string {
  if (e instanceof UploadAbortedError) return 'Upload cancelled.';
  if (e instanceof PartFailedError)
    return 'The upload keeps failing. Check your connection and try again.';
  if (!(e instanceof ApiError)) return "Couldn't upload the video. Please try again.";
  const f = (e.fields ?? {}) as Record<string, unknown>;
  switch (e.code) {
    case 'video_uploads_disabled':
      return "Video posts aren't switched on yet.";
    case 'video_requires_2fa':
      return 'Turn on two-factor authentication in Settings to post videos.';
    case 'email_unverified':
      return 'Verify your email to post videos.';
    case 'video_too_long':
      return typeof f.maxSeconds === 'number'
        ? `That video is too long for your plan (up to ${Math.round(f.maxSeconds / 60)} minutes).`
        : 'That video is too long for your plan.';
    case 'video_resolution_too_high':
      return 'That video is a higher resolution than your plan allows.';
    case 'video_too_large':
      return typeof f.limitMb === 'number'
        ? `That video is too large (max ${f.limitMb} MB).`
        : 'That video is too large.';
    case 'video_daily_limit':
      return typeof f.daily === 'number'
        ? `You've posted your ${f.daily} videos for today. Try again tomorrow.`
        : "You've reached today's video limit. Try again tomorrow.";
    case 'video_storage_quota':
      return typeof f.quotaGb === 'number'
        ? `You've used all ${f.quotaGb} GB of your video storage.`
        : "You've used all of your video storage.";
    case 'video_storage_full':
      return 'Video storage is full right now. Please try again later.';
    case 'storage_unavailable':
      return "Video storage isn't responding. Please try again in a moment.";
    case 'rate_limited':
    case 'throttled':
      return "You're starting uploads too fast. Wait a moment and try again.";
    case 'not_mp4':
    case 'not_faststart':
    case 'fragmented_mp4':
    case 'header_too_large':
    case 'no_duration':
    case 'no_dimensions':
    case 'unsupported_video_codec':
    case 'unsupported_audio_codec':
    case 'no_video_track':
    case 'multiple_video_tracks':
    case 'multiple_audio_tracks':
    case 'light_copy_required':
    case 'light_copy_too_large':
    case 'light_copy_mismatch':
      return "That video couldn't be processed. Try a different file.";
    case 'upload_incomplete':
    case 'size_mismatch':
      return "The upload didn't finish. Please try again.";
    default:
      return "Couldn't upload the video. Please try again.";
  }
}
